import { Tag } from "@superbright/react-component-library/Tag";

import { Icon } from "components";
import { dateVO, labelize } from "utils";
import { formatCost } from "utils/format";

import { ProgressiveImage } from "../ProgressiveImage";
import { Typography } from "../Typography/Typography";

import styles from "./UnitCard.module.css";

export type UnitCardProps = {
  imageUrl: string;
  name: string;
  base_price: number;
  dim_sq_ft: number;
  qty_bedrooms: number;
  qty_bathrooms: number;
  date_availability: Date | string;
  highlights: string[] | string;
  attributes?: string[];
  viewedDate?: string;
  onClick: () => void;
  isFavorite?: boolean;
  onFavoriteToggle: () => void;
  variant?: 1 | 3;
  secondaryImageUrl?: string;
  tertiaryImageUrl?: string;
  is_unit_size_visible: boolean;
  onMouseEnter?: () => void;
  /**
   * Above-the-fold cards (the first row) set this so the primary image is
   * fetched eagerly with high priority to improve LCP. Cards further down the
   * list leave it false and lazy-load instead.
   */
  priority?: boolean;
  /** Called when one of the card images fails to load */
  onImageError?: (src: string) => void;
};

export const UnitCard = ({
  onImageError,
  imageUrl,
  name,
  base_price,
  dim_sq_ft,
  qty_bedrooms,
  qty_bathrooms,
  date_availability,
  highlights,
  viewedDate,
  onClick,
  onMouseEnter,
  isFavorite = false,
  onFavoriteToggle,
  variant = 1,
  secondaryImageUrl,
  tertiaryImageUrl,
  is_unit_size_visible,
  priority = false,
}: UnitCardProps) => {
  let parsedHighlights: string[];
  if (Array.isArray(highlights)) {
    parsedHighlights = highlights;
  } else if (typeof highlights === "string") {
    parsedHighlights = highlights.split(",").map((s) => s.trim());
  } else {
    parsedHighlights = [];
  }
  function formatAvailabilityLabel(dateStr: Date | string): string {
    const date = dateVO["toDate"](
      typeof dateStr === "string" ? dateStr : dateStr.toISOString()
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date <= today) {
      return "Available Now";
    }

    return `Available ${date.getMonth() + 1}/${date.getDate()}`;
  }

  return (
    <div
      className={styles.card}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {variant === 1 ? (
        <div className={styles.singleImageWrapper}>
          <ProgressiveImage
            src={imageUrl}
            onError={onImageError}
            alt={name}
            sources={[
              {
                srcSet: imageUrl,
                media: "(max-width: 600px)",
                type: "image/avif",
              },
            ]}
            className={styles.singleImageBackground}
            priority={priority}
          />
        </div>
      ) : (
        <div className={styles.tripleImageContainer}>
          <div className={styles.primaryImageWrapper}>
            <ProgressiveImage
              src={imageUrl}
              onError={onImageError}
              alt={`${name} - primary`}
              sources={[
                {
                  srcSet: imageUrl,
                  media: "(max-width: 600px)",
                  type: "image/avif",
                },
                {
                  srcSet: imageUrl,
                  media: "(max-width: 1080px)",
                  type: "image/avif",
                },
              ]}
              className={styles.primaryImageBackground}
              priority={priority}
            />
          </div>
          <div className={styles.secondaryImagesWrapper}>
            <div className={styles.secondaryImageWrapper}>
              <ProgressiveImage
                src={secondaryImageUrl ?? ""}
                onError={onImageError}
                alt={`${name} - secondary`}
                sources={[
                  {
                    srcSet: secondaryImageUrl ?? "",
                    media: "(max-width: 600px)",
                    type: "image/avif",
                  },
                  {
                    srcSet: secondaryImageUrl ?? "",
                    media: "(max-width: 1080px)",
                    type: "image/avif",
                  },
                ]}
                className={styles.secondaryImageBackground}
              />
            </div>
            <div className={styles.secondaryImageWrapper}>
              <ProgressiveImage
                src={tertiaryImageUrl ?? ""}
                onError={onImageError}
                alt={`${name} - tertiary`}
                sources={[
                  {
                    srcSet: tertiaryImageUrl ?? "",
                    media: "(max-width: 600px)",
                    type: "image/avif",
                  },
                  {
                    srcSet: tertiaryImageUrl ?? "",
                    media: "(max-width: 1080px)",
                    type: "image/avif",
                  },
                ]}
                className={styles.secondaryImageBackground}
              />
            </div>
          </div>
        </div>
      )}

      <div className={styles.topLeftTags}>
        <div className={styles.tagsRow}>
          {parsedHighlights.map((value, index) => (
            <Tag
              key={index}
              label={labelize(value, true)[0] ?? value}
              variant="ghost"
            />
          ))}
        </div>
        {viewedDate && (
          <Tag label={`Viewed on ${viewedDate}`} variant="ghost" />
        )}
      </div>

      <button
        className={`${styles.favoriteBtn} ${
          isFavorite ? styles.favoriteBtnActive : ""
        }`}
        onClick={(e) => {
          e.stopPropagation();
          onFavoriteToggle();
        }}
        aria-pressed={isFavorite}
        aria-label={isFavorite ? "Remove from favourites" : "Add to favourites"}
      >
        <Icon name={isFavorite ? "heart-bright" : "heart"} />
      </button>

      <div className={styles.bottomInfo}>
        <div className={styles.blurGradient}>
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
        </div>
        <div className={styles.titleRow}>
          <Typography variant="body" weight="medium" color="card">
            {name}
          </Typography>
          <div className={styles.baseRentWrapper}>
            <Typography variant="body" weight="medium" color="card">
              {formatCost(base_price)}
            </Typography>
            <Typography variant="caption" color="card">
              <span className={styles.baseRent}>base rent</span>
            </Typography>
          </div>
        </div>
        <Typography variant="small" color="card">
          {qty_bedrooms === 0
            ? "Studio"
            : `${qty_bedrooms} ${qty_bedrooms === 1 ? "bed" : "beds"}`}{" "}
          &nbsp;•&nbsp; {qty_bathrooms} {qty_bathrooms === 1 ? "bath" : "baths"}{" "}
          {is_unit_size_visible && <>&nbsp;•&nbsp; {dim_sq_ft} ft² </>}
          &nbsp;•&nbsp; {formatAvailabilityLabel(date_availability)}
        </Typography>
      </div>
    </div>
  );
};
